// Shared types, deterministic score mappings, and formatting helpers for
// the Risk module (RiskPredictionView + its per-project tab panels). Kept
// in one place so every panel scores/labels signals identically.

export interface RiskSignalRow {
  id: string;
  entity_type: string;
  entity_id: string;
  rule_id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  detected_at: string;
  resolved_at: string | null;
  evidence: Record<string, unknown>;
  ai_output_id: string | null;
  previous_signal_id: string | null;
}

export interface TrendBucket {
  week: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export const SEVERITY_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, critical: 3 };
export const SEVERITY_SCORE: Record<string, number> = { low: 20, medium: 45, high: 70, critical: 90 };

export const RULE_LABELS: Record<string, string> = {
  overdue_not_done: 'Overdue, not done',
  blocked_dependency: 'Blocked dependency',
  release_health_degraded: 'Release health degraded',
};

export const humanizeRule = (ruleId: string) => RULE_LABELS[ruleId] ?? ruleId.replace(/_/g, ' ');
export const humanizeKey = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
export const formatEvidenceValue = (value: unknown) =>
  typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);

export const getRiskColor = (score: number) => {
  if (score >= 70) return 'text-destructive';
  if (score >= 50) return 'text-amber-500';
  return 'text-emerald-500';
};

export const getRiskBgColor = (score: number) => {
  if (score >= 70) return 'bg-destructive/20';
  if (score >= 50) return 'bg-amber-500/20';
  return 'bg-emerald-500/20';
};

export const getRiskLabel = (score: number) => {
  if (score >= 70) return 'Critical';
  if (score >= 50) return 'At Risk';
  if (score >= 30) return 'Moderate';
  return 'Low';
};

// RAG (red/amber/green) convention: critical and high both read as "red"
// (high is a step lighter so the two stay visually distinguishable),
// medium is amber, low is green — never the default navy Badge variant.
export const getSeverityBadgeColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'bg-destructive text-destructive-foreground';
    case 'high': return 'bg-red-400 text-white';
    case 'medium': return 'bg-amber-500 text-white';
    case 'low': return 'bg-emerald-500 text-white';
    default: return 'bg-muted text-muted-foreground';
  }
};

// Same palette as a dot, for compact severity-count rows.
export const getSeverityDotColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'bg-destructive';
    case 'high': return 'bg-red-400';
    case 'medium': return 'bg-amber-500';
    case 'low': return 'bg-emerald-500';
    default: return 'bg-muted-foreground';
  }
};

// Dedup a raw (possibly multi-row-per-finding) signals array down to the
// single latest row per (entity_type, entity_id, rule_id). evaluate-risk-rules
// inserts a *new* row on every worsening rather than updating in place, so
// the same logical finding can have several rows sharing resolved_at === null
// (only the newest is genuinely "open"; the older ones are superseded, not
// separately-open findings) — every open-signal count/list in this module
// must dedup first or it double-counts. Input must be ordered newest-first
// (detected_at desc); output preserves that order.
export const dedupLatestPerKey = (rows: RiskSignalRow[]): RiskSignalRow[] => {
  const latestByKey = new Map<string, RiskSignalRow>();
  for (const s of rows) {
    const key = `${s.entity_type}:${s.entity_id}:${s.rule_id}`;
    if (!latestByKey.has(key)) latestByKey.set(key, s);
  }
  return Array.from(latestByKey.values());
};

export const chartConfig = {
  critical: { label: 'Critical', color: 'hsl(var(--destructive))' },
  high: { label: 'High', color: '#f87171' },
  medium: { label: 'Medium', color: '#f59e0b' },
  low: { label: 'Low', color: '#10b981' },
};
